// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductActions, { ProductActionsStyle } from "@vanmeter/App/Components/Product/ProductActions";
import ProductDescription, { ProductDescriptionStyles } from "@vanmeter/Overrides/Components/ProductDescription";
import ProductPartNumbers from "@vanmeter/Overrides/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@vanmeter/Overrides/Components/ProductPrice";
import React from "react";
import { css } from "styled-components";

interface Props {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    doSearch: () => void;
    products: ProductContextModel[];
    canSeePrices?: boolean;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    header?: InjectableCss;
    headerLink?: LinkPresentationProps;
    productsContainer?: InjectableCss;
    productDescription?: InjectableCss;
    imageClickable?: ClickablePresentationProps;
    productTitle?: ProductDescriptionStyles;
    actionsWrapper?: InjectableCss;
    productPriceWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    actions?: ProductActionsStyle;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products, canSeePrices } = this.props;
        const styles = this.styles;

        // XNG-Changes: structure
        return (
            <>
                <StyledWrapper {...styles.header}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")}
                    </Typography>
                    {/** XNG-Changes */}
                    {products.length > 1 && (
                        <Link
                            onClick={() => {
                                this.props.doSearch();
                            }}
                            {...styles.headerLink}
                            // XNG-Changes
                            aria-label={translate("View All")}
                        >
                            {translate("View All")}
                        </Link>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.productsContainer}>
                    {products.map((productContext: any) => {
                        const {
                            product: {
                                id,
                                customerProductNumber,
                                productNumber,
                                manufacturerItem,
                                productTitle,
                                mediumImagePath,
                                brand,
                                upcCode,
                            },
                            productInfo: { productDetailPath },
                        } = productContext;

                        return (
                            <ProductContext.Provider value={productContext} key={id!}>
                                <StyledWrapper
                                    {...(this.props.focusedItem?.id === id
                                        ? styles.focusedContainer
                                        : styles.container)}
                                    className="autocomplete-products-container"
                                >
                                    <Clickable
                                        onClick={() => {
                                            this.props.goToUrl(productDetailPath);
                                        }}
                                        {...styles.imageClickable}
                                    >
                                        <LazyImage
                                            {...styles.image}
                                            src={mediumImagePath}
                                            className="autocomplete-products-image"
                                            // XNG-Changes
                                            altText={`autocomplete products for ${productTitle}`}
                                        />
                                    </Clickable>
                                    <StyledWrapper
                                        {...styles.infoGridItem}
                                        className="autocomplete-products-info-grid-item"
                                    >
                                        <StyledWrapper {...styles.productDescription}>
                                            {/* {product.brandDetailPagePath ? (
                                                <Link
                                                    {...styles.brandLink}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        this.props.goToUrl(product.brandDetailPagePath);
                                                    }}
                                                    data-test-selector={`productBrand_${product.id}`}
                                                >
                                                    {product.brandName}
                                                </Link>
                                            ) : (
                                                <Typography {...styles.brandText}>{product.brandName}</Typography>
                                            )} */}
                                            {/* <Link
                                                {...styles.titleLink}
                                                data-test-selector={`productId_${product.id}`}
                                                className="autocomplete-products-link"
                                            >
                                                {product.displayTitle}
                                            </Link> */}
                                            <ProductDescription
                                                product={productContext}
                                                extendedStyles={styles.productTitle}
                                            />
                                            <ProductPartNumbers
                                                productNumber={productNumber}
                                                manufacturerItem={manufacturerItem}
                                                customerProductNumber={customerProductNumber}
                                                upcCode={upcCode}
                                            />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    {/* <StyledWrapper {...styles.productNumbersContainer}>
                                        <StyledWrapper
                                            {...styles.erpNumberText}
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            <Typography {...styles.productNumberLabel}>
                                                {translate("VMI PN")}:
                                            </Typography>
                                            <Typography {...styles.productNumberValue}>
                                                {product.isNameCustomerOverride
                                                    ? product.displayName
                                                    : product.displayErpNumber}
                                            </Typography>
                                        </StyledWrapper>
                                        {product.manufacturerItemNumber && (
                                            <StyledWrapper
                                                {...styles.erpNumberText}
                                                data-test-selector="autocompleteProducts_MfgNumber"
                                                className="autocomplete-products-mfg-number"
                                            >
                                                <Typography {...styles.productNumberLabel}>
                                                    {translate("MPN")}:
                                                </Typography>
                                                <Typography {...styles.productNumberValue}>
                                                    {product.displayManufacturerItemNumber}
                                                </Typography>
                                            </StyledWrapper>
                                        )}
                                       
                                        )}
                                    </StyledWrapper> */}
                                    <StyledWrapper {...styles.actionsWrapper}>
                                        <ProductContextAvailability />
                                        {canSeePrices && (
                                            <StyledWrapper {...styles.productPriceWrapper}>
                                                <ProductPrice
                                                    showLabel={false}
                                                    showSalePriceLabel={false}
                                                    product={productContext}
                                                    extendedStyles={styles.productPriceStyles}
                                                />
                                            </StyledWrapper>
                                        )}
                                        <ProductActions extendedStyles={styles.actions} />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </ProductContext.Provider>
                        );
                    })}
                </StyledWrapper>
            </>
        );
    }
}

export default AutocompleteProducts;
